import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { ITableFilters } from '../../types/navigationTypes';
import {
  IUacRoleDto,
  IPermissionTable,
  IRoleTable,
  IPermissionDto,
  IUserTableColumns,
  IGetUserToRolePermissionDto,
  IUserToRole,
  IUserToPermission,
  IUacUser,
  IBulkUacUserPayload,
  IBulkOnboardingPayload,
  IBulkUserActionResponse,
} from '../../pages/UserAccessControl/constants/uac.interface';
import { SystemModule } from '../../enums/module';
import { TCustomerStatementFilterProps } from '../../pages/CustomerStatement/constants/CustomerReportsInterface';
import { baseQueryWithReauth } from './baseQuery';

const normalizeOptionalUniqueUserField = (
  value: string | null | undefined,
): string | null | undefined => {
  if (value == null) return value;

  const normalizedValue = value.trim();
  return normalizedValue || null;
};

const mapUacUserRegionPayload = <T extends Partial<IUacUser>>(user: T) => {
  const { regionId, RegionID, MobileNo, EmployeeCode, ...rest } = user;
  const selectedRegionId = RegionID ?? regionId;

  return {
    ...rest,
    MobileNo: normalizeOptionalUniqueUserField(MobileNo),
    EmployeeCode: normalizeOptionalUniqueUserField(EmployeeCode),
    ...(selectedRegionId !== undefined ? { RegionID: selectedRegionId } : {}),
  };
};

const userAccessControlApi = createApi({
  reducerPath: 'userAccessControl',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'UacUsers',
    'UacPermissions',
    'UacRoles',
    'UacUserMappedRoles',
    'UacUserMappedPermissions',
    'UacLockUser',
  ],
  endpoints: builder => ({
    getUacUsers: builder.query<
      BackendResponse<{ list: IUserTableColumns[]; count?: number }>,
      TCustomerStatementFilterProps
    >({
      query: params => {
        return {
          url: `/uac/users${parseQueryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: (_, err) => {
        if (err) {
          return [];
        }

        return ['UacUsers'];
      },
    }),
    checkEmailAvailability: builder.query<
      BackendResponse<void>,
      { email: string }
    >({
      query: params => ({
        url: `/uac/check-email${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),
    createUacUser: builder.mutation<BackendResponse<void>, IUacUser>({
      query: body => ({
        url: '/auth/signup',
        method: 'POST',
        body: mapUacUserRegionPayload(body),
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['UacUsers'];
      },
    }),
    createBulkUacUsers: builder.mutation<
      BackendResponse<IBulkUserActionResponse>,
      IBulkUacUserPayload
    >({
      query: body => ({
        url: '/auth/signup/bulk',
        method: 'POST',
        body: {
          ...body,
          users: body.users.map(user => mapUacUserRegionPayload(user)),
        },
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['UacUsers'];
      },
    }),
    sendBulkOnboardingMail: builder.mutation<
      BackendResponse<IBulkUserActionResponse>,
      IBulkOnboardingPayload
    >({
      query: body => ({
        url: '/auth/onboarding/bulk',
        method: 'POST',
        body,
      }),
    }),
    updateUacUser: builder.mutation<BackendResponse<void>, IUacUser>({
      query: ({ id, ...payload }) => ({
        url: `/uac/user/${id}`,
        method: 'PUT',
        body: mapUacUserRegionPayload(payload),
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['UacUsers'];
      },
    }),
    changeUacUserPassword: builder.mutation<
      BackendResponse<{
        ID: number;
        FullName: string;
        EmailID: string;
        EmployeeCode: string;
        PasswordUpdatedDate: string;
      }>,
      { userId: number; encryptedPassword: string }
    >({
      query: body => ({
        url: '/uac/users/change-password',
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['UacUsers'];
      },
    }),
    deleteUacUser: builder.mutation<BackendResponse<void>, string>({
      query: id => ({
        url: `/uac/user/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['UacUsers'];
      },
    }),
    getChangelogUacUser: builder.query<
      BackendResponse<{ list: any[]; count?: number }>,
      ITableFilters
    >({
      query: params => ({
        url: `/uac/user/changelog${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),

    getUsersToRoles: builder.query<
      BackendResponse<{ list: IGetUserToRolePermissionDto[] }>,
      number | string
    >({
      query: (id: number) => {
        return {
          url: `/uac/user-role/${id}`,
          method: 'GET',
        };
      },
      providesTags: (_, err, id) => {
        if (err) {
          return [];
        }
        return [{ type: 'UacUserMappedRoles', id }];
      },
    }),
    createSystemUserToRoles: builder.mutation<
      BackendResponse<void>,
      IUserToRole
    >({
      query: body => ({
        url: `/uac/user-role`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, error, { userId }) => {
        if (error) {
          return [];
        }
        return [{ type: 'UacUserMappedRoles', id: userId }];
      },
    }),

    getUsersToPermissions: builder.query<
      BackendResponse<{ list: IGetUserToRolePermissionDto[] }>,
      number | string
    >({
      query: (id: number) => {
        return {
          url: `/uac/user-permission/${id}`,
          method: 'GET',
        };
      },
      providesTags: (_, err, id) => {
        if (err) {
          return [];
        }
        return [{ type: 'UacUserMappedPermissions', id }];
      },
    }),
    createSystemUserToPermissions: builder.mutation<
      BackendResponse<void>,
      IUserToPermission
    >({
      query: body => ({
        url: `/uac/user-permission`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, error, { userId }) => {
        if (error) {
          return [];
        }
        return [{ type: 'UacUserMappedPermissions', id: userId }];
      },
    }),

    getSystemPermissions: builder.query<
      BackendResponse<{ list: IPermissionTable[]; count?: number }>,
      ITableFilters
    >({
      query: params => {
        return {
          url: `/uac/permission${parseQueryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: () => {
        return ['UacPermissions'];
      },
    }),
    createSystemPermission: builder.mutation<
      BackendResponse<void>,
      IPermissionDto
    >({
      query: body => {
        if (body && typeof body === 'object') {
          body = { ...body };
        }
        return {
          url: `/uac/permission`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }

        return ['UacPermissions'];
      },
    }),
    updateSystemPermission: builder.mutation<
      BackendResponse<void>,
      IPermissionDto
    >({
      query: ({ id, ...payload }) => ({
        url: `/uac/permission/${id}`,
        method: 'PUT',
        body: {
          ...payload,
        },
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }

        return ['UacPermissions', 'UacRoles'];
      },
    }),
    deleteSystemPermission: builder.mutation<BackendResponse<void>, number>({
      query: id => {
        return {
          url: `/uac/permission/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }

        return ['UacPermissions', 'UacRoles'];
      },
    }),
    getSystemRoles: builder.query<
      BackendResponse<{
        map(arg0: (role: any) => { label: any; value: any }): any;
        list: IRoleTable[];
        count?: number;
      }>,
      ITableFilters
    >({
      query: params => {
        return {
          url: `/uac/role${parseQueryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: () => {
        return ['UacRoles'];
      },
    }),
    createSystemRoles: builder.mutation<BackendResponse<void>, IUacRoleDto>({
      query: body => {
        body = {
          ...body,
          // module: SystemModule.USER_ACCESS_CONTROL,
        };
        return {
          url: '/uac/role',
          method: 'POST',
          body,
        };
      },
      invalidatesTags: () => {
        return ['UacRoles'];
      },
    }),
    updateSystemRole: builder.mutation<BackendResponse<void>, IUacRoleDto>({
      query: ({ id, ...payload }) => ({
        url: `/uac/role/${id}`,
        method: 'PUT',
        body: {
          ...payload,
          module: SystemModule.USER_ACCESS_CONTROL,
        },
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['UacRoles'];
      },
    }),
    deleteSystemRole: builder.mutation<BackendResponse<void>, number>({
      query: id => {
        return {
          url: `/uac/role/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['UacRoles'];
      },
    }),
    getAllUsersSalesOrder: builder.query<
      BackendResponse<any>,
      { isResponsibleForSalesOrder?: boolean } | void
    >({
      query: queryParams => ({
        url: `/uac/users/all${
          queryParams ? parseQueryParams(queryParams) : ''
        }`,
        method: 'GET',
      }),
    }),
    getAllUsers: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: '/uac/users/all',
        method: 'GET',
      }),
    }),
    getAllCompanyGroupedUsers: builder.query<
      BackendResponse<any>,
      Partial<{ companyGroupId: number; companyId: number }> | null
    >({
      query: query => ({
        url: `/uac/users/company-grouped${query ? parseQueryParams(query) : ''}`,
        method: 'GET',
      }),
    }),
    getEmployeeById: builder.query<BackendResponse<any>, number | string>({
      query: id => ({
        url: `/users/${id}`,
        method: 'GET',
      }),
    }),
    getAllRoles: builder.query<
      BackendResponse<{ id: number; name: string }[]>,
      void
    >({
      query: () => ({
        url: '/uac/role/all',
        method: 'GET',
      }),
      providesTags: ['UacRoles'],
    }),

    // Lock User endpoints
    getLockUserList: builder.query<
      BackendResponse<{ list: any[]; count?: number }>,
      number | string
    >({
      query: userId => ({
        url: `/uac/lock-user?userId=${userId}`,
        method: 'GET',
      }),
      providesTags: (_, err, userId) =>
        err ? [] : [{ type: 'UacLockUser' as const, id: userId }],
    }),
    getLockUserById: builder.query<BackendResponse<any>, number | string>({
      query: id => ({
        url: `/uac/lock-user/${id}`,
        method: 'GET',
      }),
    }),
    createLockUser: builder.mutation<
      BackendResponse<any>,
      {
        userId: number;
        moduleId: number;
        lockUser?: boolean;
        fromDate: string;
        toDate: string;
      }
    >({
      query: ({ userId, ...body }) => ({
        url: `/uac/lock-user?user=${userId}`,
        method: 'POST',
        body: { ...body, userId },
      }),
      invalidatesTags: (_, err, { userId }) =>
        err ? [] : [{ type: 'UacLockUser' as const, id: userId }],
    }),
    updateLockUser: builder.mutation<
      BackendResponse<any>,
      {
        id: number;
        userId?: number;
        lockUser?: boolean;
        fromDate?: string;
        toDate?: string;
        moduleId?: number;
      }
    >({
      query: ({ id, userId: _uid, ...body }) => ({
        url: `/uac/lock-user/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, err, { userId }) =>
        err ? [] : [{ type: 'UacLockUser' as const, id: userId }],
    }),
    deleteLockUser: builder.mutation<
      BackendResponse<any>,
      { id: number; userId: number }
    >({
      query: ({ id }) => ({
        url: `/uac/lock-user/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, err, { userId }) =>
        err ? [] : [{ type: 'UacLockUser' as const, id: userId }],
    }),
  }),
});

export const {
  useGetUacUsersQuery,
  useGetSystemPermissionsQuery,
  useGetSystemRolesQuery,
  useGetAllUsersSalesOrderQuery,
  useGetAllUsersQuery,
  useLazyGetAllCompanyGroupedUsersQuery,
  useGetUsersToRolesQuery,
  useCreateSystemPermissionMutation,
  useCreateSystemRolesMutation,
  useCreateSystemUserToRolesMutation,
  useCreateSystemUserToPermissionsMutation,
  useCreateUacUserMutation,
  useCreateBulkUacUsersMutation,
  useSendBulkOnboardingMailMutation,
  useDeleteSystemPermissionMutation,
  useDeleteSystemRoleMutation,
  useDeleteUacUserMutation,
  useChangeUacUserPasswordMutation,
  useUpdateSystemPermissionMutation,
  useUpdateSystemRoleMutation,
  useUpdateUacUserMutation,
  useLazyGetUsersToRolesQuery,
  useLazyGetUsersToPermissionsQuery,
  useLazyCheckEmailAvailabilityQuery,
  useGetChangelogUacUserQuery,
  useGetEmployeeByIdQuery,
  useGetAllRolesQuery,
  useLazyGetAllUsersQuery,
  useGetLockUserListQuery,
  useCreateLockUserMutation,
  useUpdateLockUserMutation,
  useDeleteLockUserMutation,
} = userAccessControlApi;

export default userAccessControlApi;
